import { EmptyTableState } from '@/modules/common/components/EmptyTableState';
import { getDefaultMRTOptions } from '@/modules/common/lib/react-material-table';
import {
  ILearningBudgetSetupInTheList,
  LearningBudgetSetupStatus,
} from '@/types/learning-budget';
import {
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { useState } from 'react';
import {
  ConfirmLearningBudgetSetupActivationModal,
  FormValues,
} from './modals/ConfirmLearningBudgetSetupActivationModal';
import { LearningBudgetSetupItemDetailTable } from './tables/LearningBudgetSetupItemDetailTable';
import { getLearningBudgetSetupsTableColumns } from './tables/LearningBudgetSetupsTable.columns';

export type ListLearningBudgetSetupsTableProps = {
  tableOptions: Omit<
    MRT_TableOptions<ILearningBudgetSetupInTheList>,
    'columns'
  >;
  onSetupUpdate: (data: ILearningBudgetSetupInTheList) => Promise<void>;
};

const defaultMRTOptions = getDefaultMRTOptions<ILearningBudgetSetupInTheList>();

export function ListLearningBudgetSetupsTable({
  tableOptions,
  onSetupUpdate,
}: ListLearningBudgetSetupsTableProps) {
  const [setupToActivate, setSetupToActivate] =
    useState<ILearningBudgetSetupInTheList | null>(null);

  const handleSetupActivate = async (setup: ILearningBudgetSetupInTheList) => {
    setSetupToActivate(setup);
  };

  const handleSetupActivateConfirmation = async (data?: FormValues) => {
    if (data?.confirmation && setupToActivate) {
      await onSetupUpdate({
        ...setupToActivate,
        status: LearningBudgetSetupStatus.Scheduled,
      });
    }

    setSetupToActivate(null);
  };

  const table = useMaterialReactTable<ILearningBudgetSetupInTheList>({
    ...defaultMRTOptions,
    columns: getLearningBudgetSetupsTableColumns(handleSetupActivate),
    muiTableProps: ({ table }) => ({
      sx: {
        height: !table.getRowCount() ? '100%' : undefined,
        flex: !table.getRowCount() ? 1 : undefined,
      },
    }),
    renderDetailPanel: ({ row }) => (
      <LearningBudgetSetupItemDetailTable
        setup={row.original}
        onSaveSetup={onSetupUpdate}
      />
    ),
    renderEmptyRowsFallback: ({ table }) => {
      const { columnFilters, globalFilter } = table.getState();
      const hasFilters =
        globalFilter ||
        columnFilters.some(columnFilter =>
          Array.isArray(columnFilter.value)
            ? columnFilter.value.length
            : columnFilter.value,
        );

      return (
        <EmptyTableState
          hasFilters={hasFilters}
          emptyStateImage="/images/no-transactions.png"
          emptyStateTitle="No learning budget cycles available"
          emptyStateMessage="To set up budget cycles, enable the Learning Budget feature and click the button to create a new cycle."
        />
      );
    },
    muiDetailPanelProps: {
      classes: {
        root: 'bg-light-gray p-0',
      },
      sx: {
        '& .MuiCollapse-root ': {
          width: '100%',
        },
      },
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 0,
        minSize: 0,
        maxSize: 0,
        header: '',
        Header: () => null,
        Cell: () => null,
        muiTableHeadCellProps: {
          classes: {
            root: '!hidden',
          },
        },
        muiTableBodyCellProps: {
          classes: {
            root: '!hidden',
          },
        },
      },
    },
    positionExpandColumn: 'last',
    enableExpandAll: false,
    enablePagination: !!tableOptions?.rowCount,
    ...tableOptions,
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <ConfirmLearningBudgetSetupActivationModal
        open={!!setupToActivate}
        setupId={setupToActivate?._id ?? ''}
        onClose={handleSetupActivateConfirmation}
      />
    </>
  );
}
